export default {
	title: 'Administration',
	subtitle: 'Manage system data',
	configStudio: {
		title: 'Config Studio',
		description:
			'Edit the active config across namespaces. Use quick editors for toggles and guided controls for operational changes.',
		environment: 'Context',
		actions: {
			export: 'Export',
			saveDraft: 'Save changes',
			promote: 'Record snapshot',
			showDiff: 'Show diff',
			hideDiff: 'Hide diff',
			resetFromProduction: 'Reset from latest saved value',
		},
		preview: 'Preview (read-only) for the current value:',
		editor: {
			title: 'Editor',
		},
		oneliners: {
			toggle: 'Toggle this setting.',
			select: 'Select a value.',
			multiselect: 'Select one or more values.',
			text: 'Enter a text value.',
			number: 'Enter a numeric value.',
			json: 'Edit JSON. Must be valid to apply.',
		},
		diff: {
			staging: 'Local draft',
			production: 'Saved value',
			added: 'Added',
			removed: 'Removed',
			changed: 'Changed',
		},
		navigationRoutes: {
			area: 'Area',
			routeKey: 'Route key',
			href: 'Href',
			showCreateButton: 'Show create button',
			requiresAuth: 'Requires auth',
			allowedRoles: 'Allowed roles',
		},
		i18nOverrides: {
			search: 'Search keys',
			newKey: 'New key',
			add: 'Add',
			noResults: 'No keys to show. Use the new key form to add an override.',
			namespace: 'Namespace',
			group: 'Group',
			overriddenOnly: 'Overridden only',
			baseValue: 'Base (EN)',
			overrideEN: 'Override (EN)',
			overrideES: 'Override (ES)',
			fillFromBase: 'Fill from base',
		},
		logging: {
			level: 'Log level',
			categories: {
				router: 'Router',
				handler: 'Handler',
				billing: 'Billing',
				webhook: 'Webhook',
				conditions: 'Conditions',
				loaders: 'Loaders',
			},
			levels: {
				trace: 'trace',
				debug: 'debug',
				info: 'info',
				warn: 'warn',
				error: 'error',
			},
		},
		sections: {
			adminPreferences: {
				title: 'Admin preferences',
				description: 'Global application settings and feature toggles.',
			},
			courseFeatures: {
				title: '{{mainEntityLabel}} features',
				description:
					'Feature toggles for {{mainEntityLabelLower}} experience and capabilities.',
			},
			navigationAdminRoutes: {
				title: 'Admin navigation routes',
				description: 'Configure admin area navigation routes and permissions.',
			},
			navigationDashboardRoutes: {
				title: 'Dashboard navigation routes',
				description: 'Configure dashboard navigation routes.',
			},
			navigationRoutes: {
				title: 'Navigation routes',
				description: 'Manage aggregated navigation routes by area and group.',
			},
			navigationPaths: {
				title: 'Navigation paths',
				description:
					'Edit canonical route paths and gating (enabled, requires auth, roles).',
			},
			navigationSidebarUser: {
				title: 'User sidebar',
				description: 'Configure items shown in the user sidebar.',
			},
			permissions: {
				title: 'Permissions',
				description: 'Role permissions and access rules.',
			},
			logging: {
				title: 'Logging',
				description: 'Application logging configuration.',
			},
			loggingAction: {
				title: 'Action logging',
				description: 'Logging options specific to user actions.',
			},
			i18nOverrides: {
				title: 'Translation overrides',
				description:
					'Manage translation overrides per locale with staging/promote workflow.',
			},
			platformImport: {
				title: 'Platform import',
			},
			marketingAcquisition: {
				title: 'Acquisition',
				description: 'Configure the primary lead magnet and acquisition flow.',
			},
		},
		groups: {
			featureToggles: 'Feature toggles',
			courseFeatureToggles: '{{mainEntityLabel}} feature toggles',
			settings: 'Settings',
			billing: {
				general: 'General',
				portal: 'Portal',
				churn: 'Churn',
				leadMagnet: 'Lead magnet',
			},
			actionLogging: {
				general: 'General',
				development: 'Development',
				production: 'Production',
				testing: 'Testing',
			},
			adminPreferences: {
				general: 'General',
				course: '{{mainEntityLabel}}',
				topic: 'Topic',
				resource: 'Resource',
				collections: 'Collections',
				userSettings: 'User settings',
				sidebar: 'Sidebar',
			},
			navigationPaths: {
				core: 'Core paths',
				auth: 'Auth paths',
				courses: '{{mainEntityLabelPlural}} paths',
				marketing: 'Marketing paths',
				publicCourses: 'Public {{mainEntityLabelPluralLower}} paths',
				admin: 'Admin paths',
				static: 'Static paths',
			},
			platformImport: {
				general: 'General settings',
				endpoints: 'Endpoints',
			},
			marketingAcquisition: {
				leadMagnet: 'Lead magnet',
			},
		},
	},
	quickSettings: {
		title: 'Settings',
		description: 'Configure your platform settings and features.',
		model: {
			description:
				'Changes saved here update the live config for the active brand. This surface does not manage a separate staging environment.',
		},
		unsavedChanges: 'Unsaved changes',
		fieldCount: '{{count}} fields',
		criticalCount: '{{count}} critical',
		warningTitle: 'Review before changing',
		importance: {
			critical: 'Critical',
			standard: 'Standard',
			advanced: 'Advanced',
		},
		entity: {
			course: 'Courses',
			topic: 'Topics',
			resource: 'Resources',
			user: 'Users',
			platform: 'Platform',
		},
		roles: {
			visibleTo: 'Visible to {{roles}}',
		},
		overview: {
			title: 'Settings overview',
			description:
				'Each section shows scope, risk level, and operational guidance before you save.',
			sectionSummary: '{{sectionCount}} sections - {{fieldCount}} fields',
			changedCount: '{{count}} with changes',
		},
		sections: {
			general: 'General',
			'general.description': 'Platform, language, and studio settings.',
			billing: 'Billing',
			'billing.description': 'Subscription and payment settings.',
			leadMagnet: 'Lead magnet',
			'leadMagnet.description':
				'Configure the main free collection used for acquisition.',
			courses: '{{mainEntityLabelPlural}}',
			'courses.description':
				'{{mainEntityLabel}} features and management options.',
			content: 'Content',
			'content.description': 'Topic, resource, and AI content settings.',
			userSettings: 'User settings',
			'userSettings.description': 'User account and settings page options.',
			navigation: 'Navigation',
			'navigation.description': 'Enable or disable routes and pages.',
		},
		groups: {
			platform: 'Platform',
			language: 'Language',
			brand: 'Brand',
			studios: 'Studios',
			permissions: 'Permissions',
			subscription: 'Subscription',
			churn: 'Churn prevention',
			portal: 'Customer portal',
			leadMagnet: 'Lead magnet',
			courseFeatures: '{{mainEntityLabel}} features',
			'courseFeatures.description':
				'Toggle {{mainEntityLabelLower}} features like study plans, diagrams, and notes.',
			courseManagement: '{{mainEntityLabel}} management',
			'courseManagement.description':
				'Control template, instance, duplicate, and export capabilities.',
			courseVisibility: '{{mainEntityLabel}} visibility',
			'courseVisibility.description':
				'Configure marketing and public preview options.',
			courseArtifacts: '{{mainEntityLabel}} artifacts',
			'courseArtifacts.description':
				'Configure default artifacts and auto-generation settings.',
			topics: 'Topics',
			'topics.description':
				'Configure topic content, resources, notes, and diagrams.',
			aiFeatures: 'AI features',
			'aiFeatures.description':
				'Enable or disable LLM-powered content generation.',
			collections: 'Collections',
			'collections.description':
				'Enable {{collectionEntityLabelPluralLower}} and collections of {{mainEntityLabelPluralLower}}.',
			settingsPages: 'Settings pages',
			'settingsPages.description':
				'Control which settings pages are available to users.',
			coreRoutes: 'Core routes',
			'coreRoutes.description':
				'Enable or disable essential application routes.',
			authRoutes: 'Authentication',
			'authRoutes.description':
				'Control login, signup, and password recovery routes.',
			courseRoutes: '{{mainEntityLabel}} routes',
			'courseRoutes.description':
				'Enable or disable {{mainEntityLabelLower}} pages.',
			adminRoutes: 'Admin routes',
			'adminRoutes.description': 'Control admin panel route visibility.',
		},
	},
	common: {
		requiresAuth: 'Requires auth',
		allowedRoles: 'Allowed roles',
		deniedRoles: 'Denied roles',
	},
	drawer: {
		createTitle: 'Create {{entity}}',
		editTitle: 'Edit {{entity}}',
		description: 'Fill the fields and save your changes.',
		selectOption: 'Select an option',
		jsonHelp: 'Valid JSON object. Leave empty to clear metadata.',
	},
	users: {
		title: 'Users',
		subtitle: 'Manage system users',
		columns: {
			name: 'Name',
			email: 'Email',
			roles: 'Roles',
			createdAt: 'Created',
			lastLogin: 'Last login',
			status: 'Status',
		},
		learning: {
			title: 'Learning',
			description:
				'Admin-only snapshot of access to {{mainEntityLabelPluralLower}} and {{collectionEntityLabelPluralLower}}.',
			loading: 'Loading learning data...',
			empty:
				'No {{mainEntityLabelPluralLower}} or {{collectionEntityLabelPluralLower}} activity found.',
			enrolledCollections: '{{collectionEntityLabelPlural}}',
			enrolledCourses: 'Catalog {{mainEntityLabelPluralLower}}',
			personalCourses: 'Personal {{mainEntityLabelPluralLower}}',
			collectionProgress: '{{completed}}/{{total}}',
			unpublished: 'Unpublished',
			personalItem: 'Personal {{mainEntityLabelLower}}',
			sources: {
				pro: 'Pro',
				courseFree: 'Free',
				collectionOpen: 'Open {{collectionEntityLabelLower}}',
				budget: 'Budget',
				grant: 'Grant',
			},
		},
		detail: {
			backToUsers: 'Back to users',
			suspendedBanner: 'This account was suspended on {{date}}.',
			suspendedReason: 'Reason: {{reason}}',
			actions: {
				impersonate: 'Impersonate',
				more: 'More actions',
				suspend: 'Suspend account',
				reactivate: 'Reactivate account',
				delete: 'Delete account',
			},
			suspendDialog: {
				title: 'Suspend this account?',
				description:
					'The user will be signed out everywhere and will not be able to log in until the account is reactivated.',
				reasonLabel: 'Reason (optional)',
			},
			reactivateDialog: {
				title: 'Reactivate this account?',
				description: 'The user will be able to log in again.',
			},
			deleteDialog: {
				title: 'Permanently delete this account?',
				description:
					'This removes the user and all their data. This action cannot be undone.',
			},
			tabs: {
				overview: 'Overview',
				learning: 'Learning',
				billing: 'Subscription',
				activity: 'Activity',
			},
			metrics: {
				courses: 'Course enrollments',
				coursesDescription: 'Catalog courses this user has joined.',
				collections: 'Collections',
				collectionsDescription: 'Learning paths in progress or completed.',
				topicsCompleted: 'Topics completed',
				topicsCompletedDescription:
					'Completed vs all topics with tracked progress.',
				activeSessions: 'Active sessions',
				activeSessionsDescription: 'Last activity: {{date}}',
			},
			identity: {
				title: 'Identity',
				description: 'Core account data.',
			},
			security: {
				title: 'Access & security',
				description: 'Login methods, sessions, and account status.',
				passkeys: 'Passkeys',
				activeSessions: 'Active sessions',
				lastSeen: 'Last session activity',
			},
			learning: {
				collections: 'Collections',
				catalogCourses: 'Catalog enrollments',
				personalCourses: 'Personal courses',
				startedAt: 'Started {{date}}',
			},
			billing: {
				empty: 'This user has no subscription history.',
				cancelsAtPeriodEnd: 'Cancels at period end',
				period: 'Current period: {{start}} → {{end}}',
				trial: 'Trial',
				canceledAt: 'Canceled',
				startedAt: 'Subscribed since',
				providerId: 'Provider subscription ID',
				plans: {
					monthly: 'Monthly plan',
					annual: 'Annual plan',
				},
			},
			activity: {
				title: 'Recent activity',
				description:
					'{{actions}} interactions and {{topics}} topics tracked in total.',
				empty: 'No recent activity recorded for this user.',
				actionLabel: '{{action}} on {{target}}',
				topicCompleted: 'Completed topic “{{title}}”',
				topicProgress: 'Worked on topic “{{title}}”',
			},
		},
	},
	roles: {
		title: 'Roles',
		subtitle: 'Manage user roles and permissions',
		columns: {
			name: 'Name',
			description: 'Description',
			permissions: 'Permissions',
			users: 'Users',
			createdAt: 'Created',
		},
	},
	preferences: {
		auth: 'Enable authentication',
		'auth.description': 'Require users to sign in to access the application.',
		'auth.warning':
			'Disabling authentication will make the app publicly accessible.',
		brand: {
			contactEmail: 'Contact email',
			'contactEmail.description': 'Primary contact email displayed to users.',
		},
	},
	billing: {
		enabled: 'Enable billing',
		'enabled.description':
			'Master switch for the billing and subscription system.',
		'enabled.warning':
			'Disabling billing will remove all subscription features.',
		server: 'Polar server mode',
		'server.description':
			'Switch between sandbox (testing) and production (live payments).',
		'server.warning':
			'Switching to production will process real payments. Ensure your polar account is properly configured.',
		'server.sandbox': 'Sandbox (testing)',
		'server.production': 'Production (live)',
		churn: {
			enabled: 'Enable churn prevention',
			'enabled.description':
				'Allow grace period access after subscription cancellation.',
			graceDays: 'Grace period days',
			'graceDays.description':
				'Number of days users retain access after cancellation (0-30).',
		},
		portal: {
			enabled: 'Enable customer portal',
			'enabled.description':
				"Allow users to manage their subscription via polar's customer portal.",
			embedded: 'Embed customer portal',
			'embedded.description':
				'Render the customer portal embedded inside the billing settings page.',
		},
		leadMagnet: {
			enabled: 'Enable lead magnet',
			'enabled.description':
				'Use the configured free collection as the default signup and onboarding lead magnet.',
			'enabled.warning':
				'If enabled without a valid collection ID, signup and marketing CTAs can degrade or point nowhere.',
			collectionId: 'Lead magnet collection ID',
			'collectionId.description':
				'Published collection ID used for signup, onboarding enrollment, and marketing prioritization.',
			'collectionId.warning':
				'Enter a valid published collection ID. Invalid IDs disable automatic enrollment safely.',
			slug: 'Lead magnet slug',
			'slug.description':
				'Optional operational slug for analytics, debugging, and future semantic linking.',
			internalName: 'Lead magnet internal name',
			'internalName.description':
				'Short internal display name used in lightweight UI references and operator context.',
			ctaLabel: 'Lead magnet CTA label',
			'ctaLabel.description':
				'Short CTA copy used in pricing and other compact acquisition surfaces.',
		},
	},
	permissions: {
		allowInstructorDelete: 'Allow instructor delete',
		'allowInstructorDelete.description':
			'Allow instructors to delete their own {{mainEntityLabelPluralLower}} and content.',
		'allowInstructorDelete.warning':
			'Enabling this allows instructors to permanently delete content.',
		allowUserCourseCreation: 'Allow user {{mainEntityLabelLower}} creation',
		'allowUserCourseCreation.description':
			'Allow regular users to create their own {{mainEntityLabelPluralLower}}.',
		requireSubscriptionForExport: 'Require subscription for export',
		'requireSubscriptionForExport.description':
			'Only allow subscribers to export {{mainEntityLabelLower}} content.',
	},
	features: {
		multiLanguage: 'Enable multi-language',
		'multiLanguage.description':
			'Allow users to switch between available languages.',
		configStudio: 'Enable Config Studio',
		'configStudio.description':
			'Show Config Studio in the admin panel for configuration management.',
		emailStudio: 'Enable Email Studio',
		'emailStudio.description':
			'Show Email Studio for configuring email templates.',
		course: {
			templates: 'Enable templates',
			'templates.description':
				'Allow creating and managing {{mainEntityLabelLower}} templates.',
			instances: 'Enable instances',
			'instances.description':
				'Allow creating {{mainEntityLabelLower}} instances from templates.',
			publicPreview: 'Enable public preview',
			'publicPreview.description':
				'Show limited {{mainEntityLabelLower}} preview to non-enrolled users.',
			studyPlan: 'Enable AI study plan',
			'studyPlan.description':
				'Allow AI-generated personalized {{mainEntityLabelLower}} study plans.',
			addModulesAndTopics: 'Enable add modules & topics',
			content: 'Enable {{mainEntityLabelLower}} content',
			'content.description':
				'Show the content tab in {{mainEntityLabelLower}} views.',
			notes: 'Enable {{mainEntityLabelLower}} notes',
			'notes.description':
				'Allow users to take notes on {{mainEntityLabelPluralLower}}.',
			diagram: 'Enable {{mainEntityLabelLower}} diagram',
			'diagram.description':
				'Show visual diagram view for {{mainEntityLabelPluralLower}}.',
			duplicate: 'Enable duplicate {{mainEntityLabelLower}}',
			'duplicate.description':
				'Allow duplicating {{mainEntityLabelPluralLower}}.',
			export: 'Enable export {{mainEntityLabelLower}}',
			'export.description': 'Allow exporting {{mainEntityLabelLower}} content.',
			marketing: 'Enable marketing',
			'marketing.description':
				'Show marketing features for {{mainEntityLabelPluralLower}}.',
			enableOriginalContentFacet: 'Enable original content facet',
			enableNotesFacet: 'Enable notes facet',
			enableLlmContent: 'Enable LLM content',
			'enableLlmContent.description':
				'Allow AI-generated content for {{mainEntityLabelPluralLower}}.',
		},
		topic: {
			content: 'Enable topic content',
			'content.description': 'Show the content area in topic views.',
			resources: 'Enable topic resources',
			'resources.description': 'Show the resources area in topic views.',
			notes: 'Enable topic notes',
			'notes.description': 'Allow users to take notes on topics.',
			diagram: 'Enable topic diagram',
			'diagram.description': 'Show visual diagram view for topics.',
			collectResources: 'Enable collect resources',
			'collectResources.description':
				'Allow collecting and attaching resources to topics.',
			manage: 'Enable topic manage',
			enableOriginalContentFacet: 'Enable original content facet',
			enableNotesFacet: 'Enable notes facet',
			enableLlmContent: 'Enable LLM content',
			'enableLlmContent.description': 'Allow AI-generated content for topics.',
		},
		resource: {
			enableOriginalContentFacet: 'Enable original content facet',
			enableNotesFacet: 'Enable notes facet',
			enableLlmContent: 'Enable LLM content',
			'enableLlmContent.description':
				'Allow AI-generated content for resources.',
		},
		collections: {
			enabled: 'Enable collections',
			'enabled.description':
				'Allow creating {{collectionEntityLabelPluralLower}} and collections of {{mainEntityLabelPluralLower}}.',
		},
	},
	settings: {
		enabled: 'Enable user settings',
		'enabled.description':
			'Show the settings area where users can manage their account.',
		routes: {
			account: 'Account page',
			'account.description': 'Show the account management page in settings.',
			appearance: 'Appearance page',
			'appearance.description': 'Show the appearance/theme settings page.',
			display: 'Display page',
			'display.description': 'Show the display settings page.',
			notifications: 'Notifications page',
			'notifications.description': 'Show the notification preferences page.',
			profile: 'Profile page',
			'profile.description': 'Show the profile editing page in settings.',
		},
	},
	artifacts: {
		autoGenerate: 'Auto-generate artifacts',
		'autoGenerate.description':
			'Automatically generate artifacts when topics are created.',
		allowUserCreation: 'Allow user artifact creation',
		'allowUserCreation.description':
			'Allow users to create their own artifacts.',
		topic: {
			defaults: 'Default topic artifacts',
			'defaults.description':
				'Artifacts to generate by default for new topics.',
		},
	},
	course: {
		coverImage: 'Cover image',
		addCover: 'Add cover',
		changeCover: 'Change cover',
		defaultTitle: '{{mainEntityLabel}}',
		features: {
			resourceViewer: {
				llm: { enabled: 'LLM in resource viewer enabled' },
			},
			progress: { enabled: '{{mainEntityLabel}} progress enabled' },
		},
	},
	featureFlags: {
		course: {
			instances: 'Flag: {{mainEntityLabelLower}} instances',
			templates: 'Flag: {{mainEntityLabelLower}} templates',
			publicPreview: 'Flag: {{mainEntityLabelLower}} public preview',
		},
	},
	help: {
		features: {
			multiLanguage: 'Enable multiple languages and language switcher.',
			configStudio: 'Show Config Studio in admin to edit app configuration.',
			emailStudio:
				'Show Email Studio in admin to configure email templates and settings.',
			course: {
				studyPlan: 'Enable AI-generated study plan tools.',
				addModulesAndTopics: 'Allow adding and organizing modules and topics.',
				content: 'Show the content area in {{mainEntityLabelLower}} screens.',
				notes: 'Enable user notes in {{mainEntityLabelLower}} screens.',
				diagram: 'Enable the diagram view for {{mainEntityLabelPluralLower}}.',
				duplicate: 'Allow duplicating a {{mainEntityLabelLower}}.',
				export: 'Allow exporting a {{mainEntityLabelLower}}.',
				templates:
					'Allow creating and managing {{mainEntityLabelLower}} templates.',
				instances:
					'Allow creating {{mainEntityLabelLower}} instances from templates.',
				marketing:
					'Enable marketing features for {{mainEntityLabelPluralLower}}.',
				publicPreview:
					'Expose a limited public preview for selected {{mainEntityLabelLower}} content.',
				enableOriginalContentFacet:
					'Show the original content facet in side panels.',
				enableNotesFacet: 'Show the notes facet in side panels.',
				enableLlmContent:
					'Expose LLM-generated content facets where available.',
			},
			topic: {
				content: 'Show the content area in topic screens.',
				resources: 'Enable the resources area in topic screens.',
				notes: 'Enable user notes in topic screens.',
				diagram: 'Enable the diagram view for topics.',
				collectResources:
					'Allow collecting and attaching resources to the topic.',
				manage: 'Enable topic management tools.',
				enableOriginalContentFacet:
					'Show the original content facet for topics.',
				enableNotesFacet: 'Show the notes facet for topics.',
				enableLlmContent: 'Expose LLM-generated content for topics.',
			},
			resource: {
				enableOriginalContentFacet:
					'Show the original content facet for resources.',
				enableNotesFacet: 'Show the notes facet for resources.',
				enableLlmContent: 'Expose LLM-generated content for resources.',
			},
			collections: {
				enabled:
					'Enable {{collectionEntityLabelPluralLower}} and related features. Allows grouping {{mainEntityLabelPluralLower}} into sequential {{collectionEntityLabelPluralLower}} or thematic collections.',
			},
		},
		preferences: {
			auth: 'Require sign-in to access the application.',
		},
		billing: {
			enabled: 'Master switch for subscriptions and billing flows.',
			polar:
				'Enable the polar integration used for checkout and subscription sync.',
			subscription: 'Expose subscription flows and entitlement checks.',
			server:
				'Choose sandbox for tests and production for real charges. Environment variables still take precedence.',
			portal: {
				enabled: 'Enable access to the customer billing portal.',
				embedded:
					'Render the customer portal embedded in settings instead of linking out.',
			},
			churn: {
				enabled:
					'Allow a grace period after cancellation before premium access is fully removed.',
				graceDays:
					'Number of days the grace period lasts after a cancellation.',
			},
			leadMagnet: {
				enabled:
					'Turn the lead magnet flow on or off without deleting its metadata.',
				collectionId:
					'Published collection ID used as the canonical lead magnet. This value powers signup, onboarding, and marketing CTAs.',
				slug: 'Optional short slug for operations, analytics, and debugging.',
				internalName:
					'Short operational name for the active lead magnet collection.',
				ctaLabel:
					'Optional short CTA label used in compact pricing and acquisition UI.',
			},
		},
		settings: {
			enabled: 'Enable the user settings area.',
			routes: {
				account: 'Show the account page in settings.',
				appearance: 'Show the appearance page in settings.',
				display: 'Show the display page in settings.',
				notifications: 'Show the notifications page in settings.',
				profile: 'Show the profile page in settings.',
			},
		},
		appSidebar: {
			courses: 'Show {{mainEntityLabelPluralLower}} in the sidebar.',
			topics: 'Show topics in the sidebar.',
			templates: 'Show templates in the sidebar.',
			favoriteCourses: 'Show favorites section in the sidebar.',
			searchContent: 'Show the content search entry in the sidebar.',
			support: 'Show support in the sidebar.',
			profile: 'Show profile in the sidebar.',
			notes: 'Show notes in the sidebar.',
		},
		course: {
			features: {
				resourceViewer: {
					llm: { enabled: 'Enable the LLM assistant in the resource viewer.' },
				},
				progress: {
					enabled: 'Track and display {{mainEntityLabelLower}} progress.',
				},
			},
		},
	},
	// Labels for logging.action fields used by Config Registry
	logging: {
		level: 'Log level',
		categories: {
			router: 'Router',
			handler: 'Handler',
			billing: 'Billing',
			webhook: 'Webhook',
			conditions: 'Conditions',
			loaders: 'Loaders',
		},
		levels: {
			trace: 'Trace',
			debug: 'Debug',
			info: 'Info',
			warn: 'Warn',
			error: 'Error',
		},
		action: {
			enabled: 'Enable action logging',
			development: {
				enabled: 'Enabled (development)',
				minLevel: 'Minimum level (development)',
				contexts: 'Contexts (array)',
				features: {
					requestDetails: 'Request details',
					responseDetails: 'Response details',
					formDataLogging: 'Log form data',
					errorStackTraces: 'Error stack traces',
					llmRequestResponse: 'LLM request/response',
					validationErrors: 'Validation errors',
					handlerTiming: 'Handler timing',
				},
			},
			production: {
				enabled: 'Enabled (production)',
				minLevel: 'Minimum level (production)',
				contexts: 'Contexts (array)',
				features: {
					requestDetails: 'Request details',
					responseDetails: 'Response details',
					formDataLogging: 'Log form data',
					errorStackTraces: 'Error stack traces',
					llmRequestResponse: 'LLM request/response',
					validationErrors: 'Validation errors',
					handlerTiming: 'Handler timing',
				},
			},
			testing: {
				enabled: 'Enabled (testing)',
				minLevel: 'Minimum level (testing)',
				contexts: 'Contexts (array)',
				features: {
					requestDetails: 'Request details',
					responseDetails: 'Response details',
					formDataLogging: 'Log form data',
					errorStackTraces: 'Error stack traces',
					llmRequestResponse: 'LLM request/response',
					validationErrors: 'Validation errors',
					handlerTiming: 'Handler timing',
				},
			},
		},
	},
	emailStudio: {
		title: 'Email Studio',
		description: 'Configure email templates and settings',
		tabs: {
			templates: 'Templates',
			settings: 'Settings',
		},
		actions: {
			save: 'Save changes',
			snapshot: 'Create snapshot',
			showDiff: 'Show diff',
			hideDiff: 'Hide diff',
		},
		messages: {
			saved: 'Changes saved live',
			promoted: 'Snapshot recorded',
		},
		errors: {
			invalidJson: 'Invalid JSON',
		},
		model: {
			title: 'Single live configuration model',
			description:
				'Saving updates the active config for this brand immediately. Snapshots create rollback points in history without moving data between environments.',
		},
		state: {
			title: 'Current state',
			description:
				'Use the live value as your editing baseline and record snapshots before risky changes.',
			currentValue: 'Current live value',
			currentVersion: 'Version {{version}}',
			latestSnapshot: 'Latest snapshot',
			snapshotVersion: 'Version {{version}}',
			noSnapshot: 'No snapshot recorded yet',
		},
		help: {
			templates:
				'Configure email templates, subjects, and per-locale overrides. Use i18n keys for subjects.',
			settings:
				'Configure email delivery settings, provider, and default addresses.',
		},
		placeholder: {
			templates: 'Enter email templates configuration as JSON...',
			settings: 'Enter email settings configuration as JSON...',
		},
		tips: {
			title: 'Tips:',
			subjects: 'Use i18n keys for subjects (e.g., "Emails.verify.subject")',
			components: 'Component names must match files in app/emails/templates/',
			validation: 'JSON will be validated before saving',
			liveModel:
				'Saving updates the live config immediately. Create a snapshot before risky changes if you want a rollback point.',
		},
		diff: {
			title: 'Current live value vs current draft',
			current: 'Current live value',
			draft: 'Draft',
		},
		editor: {
			rawJsonLabel: 'Templates JSON (current live value)',
			settingsJsonLabel: 'Settings JSON (current live value)',
		},
	},
	emailSequences: {
		title: 'Email sequences',
		description:
			'Monitor sequence delivery state, retries, and provider references.',
		empty: 'No email sequence deliveries found for the selected filters.',
		statuses: {
			pending: 'Pending',
			processing: 'Processing',
			sent: 'Sent',
			skipped: 'Skipped',
			error: 'Error',
			canceled: 'Canceled',
		},
		filters: {
			status: 'Status',
			allStatuses: 'All statuses',
			brand: 'Brand',
			allBrands: 'All brands',
			sequence: 'Sequence',
			allSequences: 'All sequences',
			limit: 'Limit',
			apply: 'Apply',
		},
		table: {
			email: 'Email',
			brand: 'Brand',
			sequence: 'Sequence',
			step: 'Step',
			status: 'Status',
			scheduledFor: 'Scheduled for',
			retryCount: 'Retries',
			providerMessageId: 'Provider message',
			lastError: 'Last error',
			actions: 'Actions',
		},
		actions: {
			retry: 'Retry',
		},
	},
	courses: {
		title: '{{mainEntityLabelPlural}}',
		subtitle: 'Manage {{mainEntityLabelPluralLower}} and templates',
		templateSummary: 'Template summary',
		totalTemplates: 'Templates',
		totalInstances: 'Instances',
		totalCourses: 'Total {{mainEntityLabelPluralLower}}',
		creation: {
			new: {
				title: 'Create new {{mainEntityLabelLower}}',
				description:
					'Follow the steps below to create a {{mainEntityLabelLower}} with AI assistance',
			},
			list: {
				title: 'Recent creations',
				description:
					'Monitor and manage {{mainEntityLabelLower}} creation history',
			},
			wizard: {
				step1: 'Entry Point',
				step2: 'Details',
				step3: 'Review',
			},
		},
		columns: {
			title: 'Title',
			category: 'Category',
			owner: 'Owner',
			isTemplate: 'Template',
			isPublished: 'Published',
			modules: 'Modules',
			enrollments: 'Enrollments',
			createdAt: 'Created',
		},
		table: {
			status: {
				published: 'Published',
				unpublished: 'Unpublished',
				template: 'Template',
				collection: '{{collectionEntityLabel}}',
			},
			readiness: {
				structureOnly: 'Structure only',
				blockingSlice: 'Blocking slice',
				fullBlueprints: 'Full blueprints',
				ready: 'Ready',
				deferred: 'Deferred',
				failed: 'Failed',
			},
		},
		filters: {
			owner: 'Owner',
			allOwners: 'All owners',
			uncategorized: 'Uncategorized',
			includeChildCourses: 'Show child courses',
		},
		actions: {
			createWizard: 'Create {{mainEntityLabelPluralLower}}',
			enrichWizard: 'Enrich {{mainEntityLabelPluralLower}}',
			bulkCreate: 'Bulk create',
			viewCreationStatus: 'Creation status',
			convertToCollection: 'Convert to collection',
		},
		dialogs: {
			createWizard: {
				title:
					'Create {{mainEntityLabelPluralLower}} and {{collectionEntityLabelPluralLower}}',
				description:
					'Configure and run a creation flow for {{mainEntityLabelPluralLower}} and {{collectionEntityLabelPluralLower}}.',
				step: 'Step',
				useCase: 'Choose creation method',
				byName: 'Create individual {{mainEntityLabelLower}}',
				byNameDescription:
					'Create one {{mainEntityLabelLower}} with a title and category. Perfect for individual creation.',
				fromCategory: 'From category',
				collection: 'Create {{collectionEntityLabelLower}}',
				collectionDescription:
					'Create a parent {{collectionEntityLabelLower}} with multiple child {{mainEntityLabelPluralLower}}. Ideal for {{collectionEntityLabelPluralLower}} or series.',
				bulkFromSubcategories: 'Bulk {{mainEntityLabelPluralLower}} creation',
				bulkFromSubcategoriesDescription:
					'Create multiple {{mainEntityLabelPluralLower}} from category subcategories. Best for populating the catalog quickly.',
				bulkCollections: 'Bulk Collections',
				bulkCollectionsDescription:
					'Create multiple collections at once from a JSON definition. Import collections generated by the skill or paste your own.',
				recommended: 'Recommended',
				presetLabel: 'Creation intent',
				presetSelect: 'Select creation intent',
				presetHelp:
					'This preset bundles workflow defaults such as structure generation, study plan generation, and diagram defaults.',
				preparationModeLabel: 'Preparation mode',
				preparationModeReviewLabel: 'Preparation mode',
				preparationModeSelect: 'Select preparation mode',
				preparationModeSummaryTitle: 'Preparation outcome',
				workflowSummaryLabel: 'Workflow summary',
				impactPreview: 'Impact preview',
				executionProfile: 'Execution profile',
				estimatedDuration: 'Estimated duration',
				modulesAndTopicsSummary: 'Modules and topics',
				booleanYes: 'Yes',
				booleanNo: 'No',
				preparationModeScope: {
					bulkCollections:
						'For collections, this mode applies to child courses; the collection container does not materialize its own topics.',
					bulkFromSubcategories:
						'In bulk creation, this mode will be applied to each course generated from the selected subcategories.',
				},
				preparationModes: {
					structureOnly: {
						label: 'Structure only',
						description:
							'Create the course shell plus modules and topics, without preparing topic blueprints or collecting resources.',
						outcome:
							'Best for shells, manual drafts, or catalog work where you want structure only.',
					},
					blockingSlice: {
						label: 'Blocking slice',
						description:
							'Prepare only the first topic so the course becomes usable immediately while later topics stay deferred.',
						outcome:
							'Useful for onboarding slices or validation flows where only the initial topic needs readiness.',
					},
					fullBlueprints: {
						label: 'Full blueprints',
						description:
							'Prepare facet blueprints for every topic while leaving resources deferred.',
						outcome:
							'Good balance when you want all topics blueprint-ready without claiming full readiness yet.',
					},
					fullReadiness: {
						label: 'Full readiness',
						description:
							'Prepare all topic blueprints and collect resources from the start.',
						outcome: 'Use when the course should feel fully ready on entry.',
					},
					contentReady: {
						label: 'Content ready',
						description:
							'Prepare blueprints, collect resources, and generate all content artifacts for every topic. The learner enters a fully consumable course.',
						outcome:
							'Best for editorial courses where the learner should find complete content from the start.',
					},
				},
				presets: {
					study_map: {
						label: 'Study map',
						description:
							'Starting pack for single-course creation. Generates modules, topics, the study plan, and enables the course diagram by default.',
					},
					fast_shell: {
						label: 'Fast shell',
						description:
							'Starting pack for quick placeholders. Keeps the course as a shell without study plan, enrichment, or diagram.',
					},
					bulk_editorial_draft: {
						label: 'Bulk editorial draft',
						description:
							'Starting pack for editorial review. Generates modules, topics, and metadata while leaving the diagram off by default.',
					},
					collection_with_child_structures: {
						label: 'Collection with child structures',
						description:
							'Starting pack for collections that should generate study structures for child courses.',
					},
					collection_shells_only: {
						label: 'Collection shells only',
						description:
							'Starting pack for collection shells that link children without generating their internal structures.',
					},
				},
				phase1Info:
					'All {{mainEntityLabelPluralLower}} will be created with phase 1 structure only (metadata + study plan)',
				titleLabel: 'Title',
				titlePlaceholder: 'Enter {{mainEntityLabelLower}} title',
				titleHelp: 'Minimum 3 characters required',
				slug: 'Slug (optional)',
				categories: 'Categories',
				categoriesPlaceholder: 'Category-1, category-2',
				categoriesHelp: 'Comma-separated category slugs',
				descriptionLabel: 'Description (optional)',
				descriptionPlaceholder:
					'Short description for this {{collectionEntityLabelLower}}',
				categorySlug: 'Category slug',
				depth: 'Depth (optional)',
				parentTitle: 'Parent title',
				parentSlug: 'Parent slug (optional)',
				children: 'Children (one title per line)',
				childrenStructureJsonLabel: 'Full structure JSON',
				childrenStructureJsonHelp:
					'Define modules and courses explicitly. Optional per-course hints: descriptionHint, outcomeHint, level, slug.',
				childrenStructureJsonParseError: 'Invalid JSON format.',
				childrenStructureJsonFormatError:
					'JSON must be an object with modules[] or courses[], or an array of modules.',
				childrenStructureCount:
					'Current count: {{count}} course{{pluralSuffix}}',
				ownerId: 'Owner ID',
				language: 'Language',
				selectLanguage: 'Select language',
				accessLevel: 'Access level',
				selectAccess: 'Select access',
				courseOptions: '{{mainEntityLabel}} options',
				courseOptionsDescription:
					'Configure basic {{mainEntityLabelLower}} settings',
				courseTypeKey: {
					label: 'Course type',
					select: 'Select course type',
					options: {
						brand_default: 'Brand default',
						blank: 'Blank',
						architect_profile: 'Architect profile',
						case_study: 'Case study',
						concept_deep_dive: 'Concept deep dive',
						tool_tutorial: 'Tool tutorial',
						professional_guide: 'Professional guide',
					},
					help: 'Blank disables brand and taxonomy editorial bias during AI generation.',
				},
				collectionTypeKey: {
					label: 'Collection type',
					select: 'Select collection type',
					help: 'Defines parent narrative style and sequencing defaults for this collection.',
				},
				collectionStructureMode: {
					label: 'Structure mode',
					select: 'Select structure mode',
					options: {
						ai_generate_structure: 'AI-generated structure',
						manual_titles_only: 'Manual titles only',
						manual_full_structure: 'Manual full structure',
					},
				},
				level: 'Level',
				selectLevel: 'Select level',
				publish: 'Publish',
				idempotencyKey: 'Idempotency key',
				generateKey: 'Generate',
				keyHelp: 'Use this key to safely retry or resume the creation process.',
				review: 'Review',
				creationOptions: 'Phase 1 creation (structure only)',
				createModulesAndTopics: 'Create modules & topics',
				createDiagramArtifact: 'Create diagram',
				createDiagramArtifactHelp:
					'Generate a Phase 1 course-level artifact. It does not change topic readiness.',
				enrichMetadata: 'Enrich metadata',
				generateStudyPlan: 'Generate study plan',
				phase1Note: 'Resources and artifacts will be added in phase 2',
				run: 'Run',
				bulkCollectionsSuccess: '{{count}} collections created successfully.',
				bulkCollectionsFailure: 'Collection creation failed.',
				bulkCollectionsPartialFailure:
					'{{succeeded}} succeeded, {{failed}} failed.',
				bulkCollectionsRequestFailed: 'Request failed with status {{status}}.',
				bulkCollectionsNetworkError: 'Network error.',
				bulkCollectionsSummary: {
					collections: 'Collections',
					totalModules: 'Total modules',
					totalCourses: 'Total courses',
					collectionBreakdown: 'Collection breakdown',
					collectionStats: '{{modules}} modules, {{courses}} courses',
					creationOptions: 'Creation options',
					note: 'Each collection will be created sequentially. This may take several minutes depending on the number of collections and courses.',
				},
				success: '{{mainEntityLabel}} creation started/completed.',
				result: 'Result',
				error: 'Error',
				// Bulk review strings
				bulkReviewSummary:
					'{{Count}} {{mainEntityLabelPluralLower}} will be created from {{parent}} subcategories',
				selectedSubcategories: 'Selected subcategories',
				createArtifacts: 'Create artifacts',
				bulkOperationNote:
					'Bulk {{mainEntityLabelLower}} creation may take several minutes. You can monitor progress in the modal that appears after submission.',
				parent: 'Parent',
				// Collection strings
				isSequential: 'Sequential collection',
				isSequentialHelp: 'Students must complete courses in order.',
				createChildrenContent: 'Create children content',
				linkChildrenToParent: 'Link children to parent',
				category: 'Category',
				selectCategory: 'Select a category',
			},
			bulkCreate: {
				title: 'Bulk create {{mainEntityLabelPluralLower}}',
				description:
					'Paste a JSON array or a newline-separated list of titles. Owner ID will be applied by default.',
				ownerId: 'Owner ID',
				input: '{{mainEntityLabelPluralLower}} input',
				preview: 'Preview',
				run: 'Run',
				result: 'Result',
			},
			convertToCollection: {
				description: 'Convert "{{course}}" into a collection or collection',
				makeSequential: 'Make sequential (collection)',
				sequentialHelp:
					'{{mainEntityLabelPlural}} will be presented in a specific order as a structured {{collectionEntityLabelLower}}.',
				collectionHelp:
					'{{mainEntityLabelPlural}} can be taken in any order as a thematic collection.',
			},
			enrichWizard: {
				title: 'Enrich {{mainEntityLabelPluralLower}}',
				description:
					'Choose the type of enrichment to apply to your {{mainEntityLabelPluralLower}}',
				steps: {
					select: 'Select enrichment',
					confirm: 'Confirm enrichment',
					progress: 'Enriching {{mainEntityLabelPluralLower}}',
					results: 'Enrichment complete',
				},
				selectDescription:
					'Select the type of enrichment to apply to your {{mainEntityLabelPluralLower}}.',
				unsplash: {
					title: 'Unsplash cover images',
					description:
						'Assign cover images from unsplash to {{mainEntityLabelPluralLower}} without images',
					features: {
						discover:
							'Auto-discovers {{mainEntityLabelPluralLower}} needing images',
						batch: 'Processes up to 50 {{mainEntityLabelPluralLower}} per run',
						rateLimit: 'Respects API rate limits',
					},
				},
				educational: {
					title: 'Educational readiness',
					description:
						'Create topic facet blueprints and curated topic resources for one course or the direct child courses of a collection.',
					targetLabel: 'Target course or collection',
					targetPlaceholder: 'Select a course or collection',
					selectedTarget: 'Selected: {{title}}',
					noTargets: 'No courses or collections are available yet.',
					preparationModeLabel: 'Preparation mode',
					preparationModeSelected: 'Preparation mode: {{mode}}',
					targetKinds: {
						collection: 'Collection',
					},
					preparationModes: {
						blockingSlice: {
							label: 'Blocking slice',
							description:
								'Prepare only the starting slice to unlock first use as quickly as possible.',
							outcome:
								'Keeps the legacy fast behavior and leaves the rest of the course for later preparation.',
						},
						fullBlueprints: {
							label: 'Full blueprints',
							description:
								'Leave every topic with facet blueprints ready for on-demand work.',
							outcome:
								'A strong balance for lead magnets or courses where each learner will build a personal experience.',
						},
						fullReadiness: {
							label: 'Full readiness',
							description:
								'Leave every topic with blueprints plus already collected and curated resources.',
							outcome:
								'The recommended ready standard for collections and courses that should feel intentionally prepared on entry.',
						},
						contentReady: {
							label: 'Content ready',
							description:
								'Prepare blueprints, collect resources, and generate all content artifacts. The learner enters a fully consumable course.',
							outcome:
								'Best for editorial courses where all content should be ready before any learner enters.',
						},
					},
					features: {
						single: 'Works for one course or a collection',
						children: 'Collection runs affect direct child courses only',
						skip: 'Already enriched child courses are skipped automatically',
					},
					confirm: {
						singleAlert:
							'This run will prepare the selected course according to the mode you selected.',
						collectionAlert:
							'This collection run will enrich direct child courses, not the collection container itself.',
						steps: {
							resolve:
								'Resolve a single course or the direct child courses of the selected collection',
							blueprints: 'Create topic facet blueprints where needed',
							resources:
								'Gather and curate topic resources for each targeted course',
							skip: 'Skip direct child courses that already meet the requested preparation mode',
						},
					},
					metrics: {
						totalTargeted: 'Total targeted',
						toEnrich: 'Will be prepared',
						skipped: 'Already prepared',
					},
					results: {
						skippedLabel: 'Skipped as already prepared',
						description:
							'Review skipped, successful, and failed course outcomes.',
					},
				},
				confirm: {
					alert:
						"This will search for and assign unsplash cover images to all {{mainEntityLabelPluralLower}} that don't currently have one. The process will handle up to 50 {{mainEntityLabelPluralLower}} in this batch.",
					whatWillHappen: 'What will happen:',
					steps: {
						discover:
							'Discover {{mainEntityLabelPluralLower}} without cover images',
						extract: 'Extract keywords from {{mainEntityLabelLower}} metadata',
						search: 'Search unsplash for matching images',
						create: 'Create artifacts with full attribution',
						update: 'Update {{mainEntityLabelLower}} cover image urls',
					},
				},
				progress: {
					title: 'Enriching {{mainEntityLabelPluralLower}}...',
					description:
						'Searching for images and updating {{mainEntityLabelPluralLower}}',
				},
				results: {
					successful: 'Successful',
					failed: 'Failed',
					remaining: 'Remaining',
					successfullyEnriched: 'Successfully enriched',
					rateLimitWarning: 'Rate limit reached!',
					rateLimitDescription:
						'Unsplash API limit exceeded. Please wait before running another enrichment batch.',
				},
				buttons: {
					cancel: 'Cancel',
					next: 'Next',
					back: 'Back',
					startEnrichment: 'Start enrichment',
					processing: 'Processing...',
					enrichMore: 'Enrich more',
					done: 'Done',
				},
				run: 'Run',
				success: 'Enrichment of {{mainEntityLabelLower}} started/completed.',
				result: 'Result',
				error: 'Error',
			},
			status: {
				title: 'Creation status',
				description: 'Query status by idempotency key or enable polling.',
				key: 'Idempotency key',
				fetch: 'Fetch',
				start: 'Start',
				stop: 'Stop',
				result: 'Result',
			},
		},
		languages: {
			es: 'Spanish',
			en: 'English',
		},
		access: {
			free: 'Free',
			subscription: 'Subscription',
			enrollment: 'Enrollment',
			preview: 'Preview',
		},
	},
	categories: {
		title: 'Categories',
		subtitle: 'Manage {{mainEntityLabelLower}} categories',
		onlyParents: 'Only parent categories',
		actions: {
			generateTaxonomy: 'Generate taxonomy',
			import: 'Import categories',
			enrichEditorialWizard: 'Enrich categories',
		},
		dialogs: {
			enrichWizard: {
				title: 'Enrich categories',
				description:
					'Choose the type of enrichment to apply to your categories',
				steps: {
					select: 'Select enrichment',
					confirm: 'Confirm enrichment',
					progress: 'Enriching categories',
					results: 'Enrichment complete',
				},
				selectDescription:
					'Select the type of enrichment to apply to your categories.',
				editorial: {
					title: 'Editorial metadata',
					description:
						'Generate category/subcategory editorial register and technicality for prompt composition.',
				},
				confirm: {
					alert:
						'This will generate editorial metadata for subcategories missing it. The process handles a small batch per run.',
					whatWillHappen: 'What will happen:',
					steps: {
						discover: 'Discover subcategories missing editorial metadata',
						generate:
							'Generate register/technicality and writing rules via LLM',
						store: 'Store results in category.metadata.editorial',
					},
				},
				progress: {
					title: 'Enriching categories...',
					description: 'Generating editorial metadata and updating categories',
				},
				results: {
					successful: 'Successful',
					failed: 'Failed',
					remaining: 'Remaining',
					successfullyEnriched: 'Successfully enriched',
				},
				buttons: {
					cancel: 'Cancel',
					next: 'Next',
					back: 'Back',
					startEnrichment: 'Start enrichment',
					processing: 'Processing...',
					enrichMore: 'Enrich more',
					done: 'Done',
				},
			},
		},
		taxonomy: {
			dialog: {
				title: 'Generate category taxonomy',
				mode: {
					bulk: {
						title: 'Bulk import',
						description:
							'Manually enter or paste a list of categories and subcategories.',
					},
					llm: {
						title: 'Create with AI',
						description: 'Generate a complete taxonomy structure using AI.',
						blocked:
							'AI generation is only available for initial taxonomy creation. Use bulk import to add more categories.',
					},
				},
				fields: {
					mode: 'Generation mode',
					subjectArea: 'Subject area',
					subjectAreaPlaceholder: 'e.g. Web development, data science',
					numCategories: 'Main categories',
					minSubcategories: 'Min subcategories',
					maxSubcategories: 'Max subcategories',
					language: 'Language',
					hierarchyText: 'Review & edit hierarchy',
					hierarchyHelp:
						'Edit the text below to modify the taxonomy. Use indentation (hyphen) for subcategories.',
					hierarchyPlaceholder:
						'Category 1\\n- subcategory 1\\n- subcategory 2\\nCategory 2\\n- subcategory A',
				},
				description: {
					step1: 'Configure generation settings',
					step2: 'Review and edit the generated taxonomy',
				},
				steps: {
					configuration: 'Configuration',
					review: 'Review & create',
				},
				buttons: {
					generatePreview: 'Generate preview',
				},
				validation: {
					subjectAreaRequired: 'Subject area is required',
					minMaxInvalid:
						'Min subcategories must be less than or equal to max subcategories',
				},
				errors: {
					parsingFailed: 'Failed to parse category hierarchy',
					invalidCharacters:
						'Category names can only contain letters, numbers, spaces, dashes, ampersands, and parentheses',
					emptyName: 'Category name cannot be empty',
					duplicateName: 'Duplicate category name found',
					noCategories: 'No categories found in the input text',
					multipleErrors: '{{Count}} errors found',
				},
			},
			confirm: {
				alert:
					"This will search for and assign unsplash cover images to all {{mainEntityLabelPluralLower}} that don't currently have one. The process will handle up to 50 {{mainEntityLabelPluralLower}} in this batch.",
				whatWillHappen: 'What will happen:',
				steps: {
					discover:
						'Discover {{mainEntityLabelPluralLower}} without cover images',
					extract: 'Extract keywords from {{mainEntityLabelLower}} metadata',
					search: 'Search unsplash for matching images',
					create: 'Create artifacts with full attribution',
					update: 'Update {{mainEntityLabelLower}} cover image urls',
				},
			},
			progress: {
				title: 'Enriching {{mainEntityLabelPluralLower}}...',
				description:
					'Searching for images and updating {{mainEntityLabelPluralLower}}',
			},
			results: {
				successful: 'Successful',
				failed: 'Failed',
				remaining: 'Remaining',
				successfullyEnriched: 'Successfully enriched',
				rateLimitWarning: 'Rate limit reached!',
				rateLimitDescription:
					'Unsplash API limit exceeded. Please wait before running another enrichment batch.',
			},
			buttons: {
				cancel: 'Cancel',
				next: 'Next',
				back: 'Back',
				startEnrichment: 'Start enrichment',
				processing: 'Processing...',
				enrichMore: 'Enrich more',
				done: 'Done',
			},
			run: 'Run',
			success: 'Enrichment of {{mainEntityLabelLower}} started/completed.',
			result: 'Result',
			error: 'Error',
		},
		status: {
			title: 'Creation status',
			description: 'Query status by idempotency key or enable polling.',
			key: 'Idempotency key',
			fetch: 'Fetch',
			start: 'Start',
			stop: 'Stop',
			result: 'Result',
		},
	},
	languages: {
		es: 'Spanish',
		en: 'English',
	},
	access: {
		free: 'Free',
		subscription: 'Subscription',
		enrollment: 'Enrollment',
		preview: 'Preview',
	},
}
